import { Control, Controller, RegisterOptions } from 'react-hook-form';
import { TextField } from '@mui/material';
import MuiAutocomplete, { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import { ComponentProps, ReactNode } from 'react';
import { useAutocompletePopperStyles } from './autocomplete.styles';

export interface AutocompleteProps {
  control: Control;
  name: string;
  rules?: RegisterOptions;
  defaultValue?: any;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  required?: string | boolean;
  placeholder?: string;
  fullWidth?: boolean;
  helperText?: string;
  inputStyles?: React.CSSProperties;
  options: any[];
  getOptionLabel?: (option: any) => any;
  getOptionSelected?: (option: any, value: any) => boolean;
  renderOption?: (option: any, state: AutocompleteRenderOptionState) => ReactNode;
  renderTags?: (value: any[], getItemProps: (args?: { index?: number }) => any) => ReactNode;
  // Material UI v9 moved TextField's InputProps to slotProps.input.
  InputProps?: NonNullable<ComponentProps<typeof TextField>['slotProps']>['input'];
  disableClearable?: boolean;
}

export const FreeSoloAutocomplete = ({
  control,
  name,
  rules,
  label = '',
  loading = false,
  disabled = false,
  required = false,
  placeholder = '',
  fullWidth = false,
  helperText = '',
  inputStyles,
  options,
  defaultValue,
  getOptionLabel,
  getOptionSelected,
  renderOption,
  renderTags,
  InputProps,
  disableClearable = false
}: AutocompleteProps) => {
  const popperClasses = useAutocompletePopperStyles();
  return (
    <Controller
      control={control}
      name={name}
      rules={{ required, ...rules }}
      render={({ field: { onChange, value, ...rest }, fieldState: { error: fieldError } }) => {
        return (
          <MuiAutocomplete
            classes={{ popper: popperClasses.popper }}
            {...rest}
            freeSolo
            id={name}
            loading={loading}
            disabled={disabled}
            disableClearable={disableClearable}
            options={options}
            openOnFocus
            defaultValue={defaultValue ?? ''}
            inputValue={value ?? ''}
            onChange={(_e, data) => onChange({ target: { value: data } })}
            onInputChange={(_e, data) => onChange({ target: { value: data } })}
            fullWidth={fullWidth}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={getOptionSelected}
            renderOption={
              renderOption
                ? (props, option, state) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        {renderOption(option, state)}
                      </li>
                    );
                  }
                : undefined
            }
            renderValue={renderTags}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  name={name}
                  value={value}
                  defaultValue={defaultValue}
                  style={inputStyles}
                  placeholder={placeholder}
                  required={!!required}
                  error={!!fieldError}
                  label={label}
                  helperText={fieldError ? fieldError.message : helperText}
                  slotProps={{
                    ...params.slotProps,
                    input: { ...params.slotProps.input, ...InputProps }
                  }}
                />
              );
            }}
          />
        );
      }}
    />
  );
};
